import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Cloud, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

// C3 — Voice consent revocation surface.
//
// Client-side ONLY. localStorage is the source of truth for both keys:
//
//   sz_voice_consent_v1        — native browser SpeechRecognition (device
//                                 speech APIs). Values: "granted" | "denied"
//                                 | "" (never asked yet).
//
//   sz_voice_server_consent_v1 — server transcription fallback via
//                                 /api/voice/transcribe. Values: same set.
//
// This section deliberately does NOT hit the backend:
//   * There is no backend record to delete for voice consent — the
//     platform never persists raw audio (see /api/voice/transcribe:
//     the buffer lives in memory only). Any UI copy implying "your
//     recordings will be deleted from our servers" would be misleading.
//   * Consent flags live in localStorage on this browser only. This is
//     honest: revoking here revokes on this device/browser and takes
//     effect the next time the student taps the mic on this device.
//
// Cross-tab enforcement:
//   * A ``StorageEvent`` fires in every OTHER open tab on this origin
//     when we write to localStorage. Chat.jsx listens for that and
//     immediately calls voice.stop() + voice.reset() so any active
//     mic session on another tab stops as soon as the student revokes
//     consent here. Same tab handles cancellation directly (see
//     ``revoke`` below).
//
// Governance: no vendor/model/engine identifiers surfaced. All copy is
// Turkish and links /gizlilik for the underlying legal detail.

const NATIVE_KEY = "sz_voice_consent_v1";
const SERVER_KEY = "sz_voice_server_consent_v1";

function readFlag(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeFlag(key, value) {
  try {
    // Setting to the SAME value does not fire a StorageEvent — first
    // clear then set, so listeners in the same-origin tabs always see
    // a change signal. Empty string means "never asked" and re-opens
    // the one-time consent card on next tap.
    localStorage.removeItem(key);
    if (value) localStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

// The Chat page listens for this same-tab custom event and reacts
// identically to the cross-tab StorageEvent — see Chat.jsx.
const CONSENT_EVENT = "sz-voice-consent-change";

function emitLocalChange(key) {
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { key } }));
  } catch {
    /* noop */
  }
}

export function ProfileVoicePrivacy() {
  const [native, setNative] = useState(() => readFlag(NATIVE_KEY));
  const [server, setServer] = useState(() => readFlag(SERVER_KEY));

  // Reflect changes made in other tabs so the toggle labels stay in
  // sync without the student having to refresh this page.
  useEffect(() => {
    const onStorage = (e) => {
      if (!e || !e.key) return;
      if (e.key === NATIVE_KEY) setNative(e.newValue || "");
      if (e.key === SERVER_KEY) setServer(e.newValue || "");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const revoke = (key, setter, label) => {
    writeFlag(key, "denied");
    setter("denied");
    emitLocalChange(key);
    toast.success(
      `${label} izni bu tarayıcıda kapatıldı. Yeniden açana kadar sesli giriş kullanılmaz.`,
    );
  };
  const enable = (key, setter, label) => {
    writeFlag(key, "granted");
    setter("granted");
    emitLocalChange(key);
    toast.success(`${label} izni bu tarayıcıda açıldı.`);
  };
  const reset = (key, setter, label) => {
    writeFlag(key, "");
    setter("");
    emitLocalChange(key);
    toast.info(`${label} tercihi sıfırlandı. Bir sonraki mikrofon tıklamasında sana yeniden sorulur.`);
  };

  return (
    <div
      className="border border-zinc-200 rounded-2xl bg-white p-6 space-y-5"
      data-testid="profile-voice-privacy"
    >
      <div>
        <h2 className="font-display text-xl font-semibold text-zinc-900">
          Sesli giriş izinleri
        </h2>
        <p className="mt-1 text-sm text-zinc-600">
          İzinler yalnızca bu tarayıcıda saklanır. Bu ekrandaki kararların
          hemen etkindir; sesli girişi kapatırsan mikrofon bir sonraki
          tıklamada devreye girmez. Detaylar için{" "}
          <Link
            to="/gizlilik"
            className="underline underline-offset-2 text-[#1E3A8A] hover:text-[#1E40AF]"
            data-testid="profile-voice-privacy-link"
          >
            Gizlilik Politikası
          </Link>
          .
        </p>
      </div>

      {/* Native browser speech recognition */}
      <ConsentRow
        testidPrefix="voice-native"
        icon={<Mic className="w-4 h-4 text-[#1E3A8A]" aria-hidden />}
        title="Cihaz / tarayıcı sesli girişi"
        description="Konuşman tarayıcın veya cihazın kendi ses tanıma servisi tarafından yazıya çevrilir. Bu servisler sesi kendi platform davranışlarına göre işleyebilir; SuperZeka bu sürümde ham ses kaydını bilerek saklamaz."
        state={native}
        onEnable={() => enable(NATIVE_KEY, setNative, "Cihaz sesli girişi")}
        onRevoke={() => revoke(NATIVE_KEY, setNative, "Cihaz sesli girişi")}
        onReset={() => reset(NATIVE_KEY, setNative, "Cihaz sesli girişi")}
      />

      {/* Server transcription fallback */}
      <ConsentRow
        testidPrefix="voice-server"
        icon={<Cloud className="w-4 h-4 text-[#1E3A8A]" aria-hidden />}
        title="Sunucu üzerinden yazıya çevirme (yedek)"
        description="Bazı tarayıcılar sesli girişi tek başına yapamaz. Bu yedek yolda sesin, yalnızca yazıya çevirmek için SuperZeka sunucusuna gönderilir. SuperZeka ham ses kaydını bilerek saklamaz; sohbete yalnızca çevrilen metin gider. Kayıt tek seferliktir, en fazla 30 saniye sürer."
        state={server}
        onEnable={() => enable(SERVER_KEY, setServer, "Sunucu üzerinden yazıya çevirme")}
        onRevoke={() => revoke(SERVER_KEY, setServer, "Sunucu üzerinden yazıya çevirme")}
        onReset={() => reset(SERVER_KEY, setServer, "Sunucu üzerinden yazıya çevirme")}
      />

      <div
        className="flex gap-2 rounded-xl bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-xs text-zinc-700 leading-relaxed"
        data-testid="voice-privacy-note"
      >
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" aria-hidden />
        <div>
          SuperZeka <strong>ham ses kayıtlarını sunucuda saklamaz</strong>;
          bu nedenle burada silinecek bir sunucu kaydı yoktur. Bu ekran
          bu tarayıcıdaki iznini kontrol eder.
        </div>
      </div>
    </div>
  );
}

function ConsentRow({ testidPrefix, icon, title, description, state, onEnable, onRevoke, onReset }) {
  const isGranted = state === "granted";
  const isDenied = state === "denied";
  const isUnset = !isGranted && !isDenied;
  const stateLabel = isGranted ? "Açık" : isDenied ? "Kapalı" : "Henüz sorulmadı";
  const stateTone = isGranted
    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
    : isDenied
      ? "bg-zinc-100 border-zinc-200 text-zinc-700"
      : "bg-amber-50 border-amber-200 text-amber-800";

  return (
    <div
      className="rounded-xl border border-zinc-200 p-4 flex flex-col sm:flex-row sm:items-start gap-3"
      data-testid={`${testidPrefix}-row`}
      data-consent-state={state || "unset"}
    >
      <div className="w-9 h-9 shrink-0 rounded-xl bg-[#1E3A8A]/5 border border-[#1E3A8A]/10 flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm font-semibold text-zinc-900" data-testid={`${testidPrefix}-title`}>
            {title}
          </div>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${stateTone}`}
            data-testid={`${testidPrefix}-state`}
            aria-live="polite"
          >
            {isGranted ? <CheckCircle2 className="w-3 h-3" /> : isDenied ? <XCircle className="w-3 h-3" /> : null}
            {stateLabel}
          </span>
        </div>
        <p
          className="mt-1 text-[12.5px] text-zinc-600 leading-relaxed"
          data-testid={`${testidPrefix}-description`}
        >
          {description}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {isGranted ? (
            <button
              type="button"
              onClick={onRevoke}
              className="h-8 px-3 rounded-full text-xs font-medium bg-white border border-zinc-300 text-zinc-800 hover:border-zinc-400"
              data-testid={`${testidPrefix}-revoke`}
              aria-label={`${title} iznini kapat`}
            >
              İzni kapat
            </button>
          ) : (
            <button
              type="button"
              onClick={onEnable}
              className="h-8 px-3 rounded-full text-xs font-medium bg-[#1E3A8A] text-white hover:bg-[#1E40AF]"
              data-testid={`${testidPrefix}-enable`}
              aria-label={`${title} iznini aç`}
            >
              İzni aç
            </button>
          )}
          {!isUnset && (
            <button
              type="button"
              onClick={onReset}
              className="h-8 px-3 rounded-full text-xs font-medium bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300"
              data-testid={`${testidPrefix}-reset`}
              aria-label={`${title} tercihini sıfırla`}
            >
              Sıfırla
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
